import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { JOB_STATUS } from '../services/jobService';
import { saveJob, unsaveJob, isJobSaved } from '../services/savedJobsService';
import { getProfileStats } from '../services/seekerProfileService';
import { applyForJob, checkExistingApplication } from '../services/applicationService';
// import { checkPackageLimit } from '../services/subscriptionService';

// Add line-clamp utility styles
const lineClampStyles = `
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('line-clamp-styles')) {
  const style = document.createElement('style');
  style.id = 'line-clamp-styles';
  style.textContent = lineClampStyles;
  document.head.appendChild(style);
}

const JobCard = ({ job, onStatusChange, onDelete, onEdit, onViewApplications, applicationCount, showActions = false, onClick, user, userData, onJobSaved, onJobUnsaved, onApplyClick }) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (user && job.id) {
      checkIfJobSaved();
      checkIfJobApplied();
    }
  }, [user, job.id]);

  const checkIfJobSaved = async () => {
    try {
      const result = await isJobSaved(user.uid, job.id);
      if (result.success) {
        setSaved(result.isSaved);
      }
    } catch (error) {
      console.error('Error checking if job is saved:', error);
    }
  };

  const checkIfJobApplied = async () => {
    try {
      const result = await checkExistingApplication(job.id, user.uid);
      setApplied(result.exists);
    } catch (error) {
      console.error('Error checking if job is applied:', error);
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/jobs/${job.id}`);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (saved) {
        const result = await unsaveJob(user.uid, job.id);
        if (result.success) {
          setSaved(false);
          if (onJobUnsaved) {
            onJobUnsaved(job.id);
          }
        }
      } else {
        const result = await saveJob(user.uid, job.id);
        if (result.success) {
          setSaved(true);
          if (onJobSaved) {
            onJobSaved(job.id);
          }
        }
      }
    } catch (error) {
      console.error('Error toggling save job:', error);
    }
  };

  const handleApply = async (e) => {
    e.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }

    if (userData?.userType !== 'jobseeker') {
      return;
    }

    if (applied) {
      // If already applied, navigate to application history
      navigate('/application-history');
      return;
    }

    // Subscription checks disabled temporarily to allow free applications for job seekers.
    // try {
    //   const limitCheck = await checkPackageLimit(user.uid, 'application');
    //   
    //   if (!limitCheck.success) {
    //     if (limitCheck.needsSubscription) {
    //       const confirmPurchase = window.confirm(
    //         '❌ No Active Subscription\n\n' +
    //         'You need an active subscription to apply for jobs.\n\n' +
    //         'Would you like to view our packages?'
    //       );
    //       if (confirmPurchase) {
    //         navigate('/packages');
    //       }
    //       return;
    //     } else if (limitCheck.limitReached) {
    //       const confirmUpgrade = window.confirm(
    //         '❌ Application Limit Reached\n\n' +
    //         `You have used all ${limitCheck.max} applications in your current package.\n\n` +
    //         'Would you like to upgrade your package?'
    //       );
    //       if (confirmUpgrade) {
    //         navigate('/packages');
    //       }
    //       return;
    //     }
    //   }
    // } catch (error) {
    //   console.error('Error checking subscription:', error);
    //   alert('Failed to verify subscription. Please try again.');
    //   return;
    // }

    // Check if profile is complete before showing modal
    try {
      const profileStats = await getProfileStats(user.uid);
      if (profileStats.success && profileStats.stats.completionPercentage < 85) {
        if (window.confirm('Your profile is not complete. You need at least 85% completion to apply for jobs. Would you like to complete your profile now?')) {
          navigate('/seeker-profile');
        }
        return;
      }
    } catch (error) {
      console.error('Error checking profile completion:', error);
    }

    // Use parent's onApplyClick if provided
    if (onApplyClick) {
      onApplyClick(job, setApplying, setApplied);
    }
  };
  const getStatusBadge = (status) => {
    const statusConfig = {
      [JOB_STATUS.ACTIVE]: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      [JOB_STATUS.DRAFT]: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      [JOB_STATUS.PAUSED]: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Paused' },
      [JOB_STATUS.CLOSED]: { bg: 'bg-red-100', text: 'text-red-800', label: 'Closed' },
      [JOB_STATUS.EXPIRED]: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Expired' }
    };
    
    const config = statusConfig[status] || statusConfig[JOB_STATUS.DRAFT];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatSalary = (salary) => {
    if (!salary.min && !salary.max) return 'Salary not disclosed';
    
    const formatAmount = (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: salary.currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    };
    
    if (salary.min && salary.max) {
      return `${formatAmount(salary.min)} - ${formatAmount(salary.max)} ${salary.period || 'yearly'}`;
    } else if (salary.min) {
      return `From ${formatAmount(salary.min)} ${salary.period || 'yearly'}`;
    } else {
      return `Up to ${formatAmount(salary.max)} ${salary.period || 'yearly'}`;
    }
  };

  const formatLocation = (location) => {
    let locationStr = `${location.city}, ${location.state}`;
    if (location.remote && location.hybrid) {
      locationStr += ' (Remote/Hybrid)';
    } else if (location.remote) {
      locationStr += ' (Remote)';
    } else if (location.hybrid) {
      locationStr += ' (Hybrid)';
    }
    return locationStr;
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return '1 day ago';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  return (
    <div 
      className={`group bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-xl hover:border-blue-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden`}
      onClick={handleCardClick}
    >
      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full -translate-y-16 translate-x-16 opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
      
      {/* Header */}
      <div className="flex justify-between items-start mb-4 relative z-10">
        <div className="flex-1">
          {/* Company Logo */}
          <div className="flex items-start gap-4 mb-3">
            <div className="w-12 h-12 rounded-xl shadow-lg overflow-hidden">
              {job.companyLogo ? (
                <img 
                  src={job.companyLogo} 
                  alt={job.company}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                  {job.company.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 mb-1 flex-wrap">
                <h3 className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors group-hover:text-blue-700 line-clamp-2 break-words">{job.title}</h3>
                <div className="flex gap-1 flex-shrink-0">
                  {job.featured && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md animate-pulse whitespace-nowrap">
                      ⭐ Featured
                    </span>
                  )}
                  {job.urgent && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md animate-pulse whitespace-nowrap">
                      🔥 Urgent
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-gray-700 font-semibold text-base truncate">{job.company}</p>
                {job.industry && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 border border-indigo-200 whitespace-nowrap">
                    {job.industry}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(job.status)}
        </div>
      </div>

      {/* Job Details */}
      <div className="space-y-4 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-w-0">
            <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm font-medium text-gray-700 truncate">{formatLocation(job.location)}</span>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-w-0">
            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
            </svg>
            <span className="text-sm font-medium text-gray-700 truncate">{job.jobType}</span>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-w-0">
            <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="text-sm font-medium text-gray-700 truncate">{job.workMode}</span>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-w-0">
            <svg className="w-4 h-4 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-sm font-medium text-gray-700 truncate">{job.experienceLevel}</span>
          </div>

          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-w-0">
            <svg className="w-4 h-4 text-pink-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M5 7v12a2 2 0 002 2h10a2 2 0 002-2V7M8 7V4a2 2 0 012-2h4a2 2 0 012 2v3" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 12h4M10 16h4" />
            </svg>
            <span className="text-sm font-medium text-gray-700 truncate">Vacancies: {job.vacancies || 1}</span>
          </div>
        </div>

        {/* Salary */}
        {(job.salary?.min || job.salary?.max) && (
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border border-green-200 rounded-xl shadow-sm min-w-0">
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <span className="text-base font-bold text-green-800 truncate">{formatSalary(job.salary)}</span>
          </div>
        )}

        {/* Description Preview */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-700 text-sm leading-relaxed line-clamp-2">
            {job.description}
          </p>
        </div>

        {/* Skills */}
        {job.requiredSkills && job.requiredSkills.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Required Skills</h4>
            <div className="flex flex-wrap gap-2">
              {job.requiredSkills.slice(0, 5).map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200 hover:from-blue-200 hover:to-indigo-200 transition-colors max-w-32 truncate"
                  title={skill}
                >
                  {skill}
                </span>
              ))}
              {job.requiredSkills.length > 5 && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-gray-100 to-slate-100 text-gray-700 border border-gray-200 whitespace-nowrap">
                  +{job.requiredSkills.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 relative z-10">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Posted {getTimeAgo(job.createdAt)}</span>
          </div>
          {showActions && (
            <>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>{applicationCount || job.stats?.applications || 0} applications</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 616 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>{job.stats?.views || 0} views</span>
              </div>
            </>
          )}
        </div>
        
        {showActions && (
          <div className="flex items-center gap-2">
            {/* Status Dropdown */}
            {onStatusChange && (
              <select
                value={job.status}
                onChange={(e) => {
                  e.stopPropagation();
                  onStatusChange(job.id, e.target.value);
                }}
                className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                onClick={(e) => e.stopPropagation()}
              >
                <option value={JOB_STATUS.ACTIVE}>Active</option>
                <option value={JOB_STATUS.PAUSED}>Paused</option>
                <option value={JOB_STATUS.CLOSED}>Closed</option>
                <option value={JOB_STATUS.DRAFT}>Draft</option>
              </select>
            )}
            
            {/* Action Buttons */}
            <div className="flex items-center gap-1">
              {/* View Applications Button */}
              {onViewApplications && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewApplications(job.id);
                  }}
                  className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                  title="View Applications"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </button>
              )}
              
              {/* Edit Button */}
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(job.id);
                  }}
                  className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                  title="Edit Job"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
              
              {/* Delete Button */}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(job.id);
                  }}
                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Job"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
        
        {!showActions && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={handleSave}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium ${
                saved 
                  ? 'text-red-500 bg-red-50 hover:bg-red-100 border border-red-200' 
                  : 'text-gray-600 bg-gray-50 hover:text-red-500 hover:bg-red-50 border border-gray-200 hover:border-red-200'
              }`}
              title={saved ? "Saved" : "Save job"}
            >
              <svg className="w-4 h-4" fill={saved ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span className="sm:hidden">{saved ? 'Saved' : 'Save Job'}</span>
            </button>
            {user && userData?.userType === 'jobseeker' && (
              <button
                onClick={handleApply}
                disabled={applying}
                className={`flex-1 sm:flex-none px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 ${
                  applied 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600' 
                    : applying
                    ? 'bg-gray-400 text-white cursor-not-allowed transform-none'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                }`}
              >
                {applying ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Applying...</span>
                  </>
                ) : applied ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Applied</span>
                  </>
                ) : (
                  <>
                    <span>Apply Now</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobCard;
